import type {
  RoutingLoadCapSource,
  RoutingLoadEffectiveLayerEntry,
  RoutingLoadEffectiveLayers,
  RoutingLoadOverviewTarget,
} from '@nadoutong/contracts';
import { useRoutingLoadOverview } from '../../api/useRoutingLoad.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import type { ChipTone } from '../../ui/index.ts';
import { isUnknownLayerScope, layerEntryKey } from './loadScopeFields.ts';

const BREAKER_TONE: Record<RoutingLoadOverviewTarget['breaker_state'], ChipTone> = {
  closed: 'ok',
  open: 'bad',
  half_open: 'warn',
  forced: 'warn',
};

function shortId(value: string | null): string {
  if (value === null || value.length === 0) return '—';
  if (value.length <= 14) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function formatCap(
  cap: number | null,
  source: RoutingLoadCapSource | null,
  confidence: number | null,
  unlimited: string,
  sourceLabel: (source: RoutingLoadCapSource) => string,
  confidenceLabel: (value: string) => string,
): string {
  if (cap === null) return unlimited;
  const sourceText = source === null ? '' : ` · ${sourceLabel(source)}`;
  const confidenceText =
    source === 'inferred' && confidence !== null
      ? ` · ${confidenceLabel(String(confidence))}`
      : '';
  return `${cap}${sourceText}${confidenceText}`;
}

function targetKey(target: RoutingLoadOverviewTarget): string {
  return [
    target.upstream_id,
    target.account_id ?? '',
    target.key_bucket_id ?? '',
    target.upstream_channel_id ?? '',
  ].join(':');
}

export function RoutingLoadTargetsView(): JSX.Element {
  const t = useT();
  const overview = useRoutingLoadOverview();
  const rows = overview.data?.targets ?? [];

  if (overview.isPending) {
    return (
      <p className="m-0 text-body text-paper-low" data-testid="routing-load-targets-loading">
        {t('tenant_routing.load.runtime.loading')}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p
        className="m-0 rounded bg-raised px-3 py-2 text-dense text-paper-low"
        data-testid="routing-load-targets-empty"
      >
        {t('tenant_routing.load.runtime.empty')}
      </p>
    );
  }

  return (
    <Table testId="routing-load-targets-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('tenant_routing.load.runtime.table.identity')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.rpm')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.tpm')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.cap')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.shed')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.breaker')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.ttft')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.rehomed')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.r429_rehomed')}</Th>
          <Th>{t('tenant_routing.load.runtime.table.layers')}</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((target) => (
          <Tr key={targetKey(target)} interactive={false} data-testid={`routing-load-target-${target.upstream_id}`}>
            <Td>
              <span className="block truncate font-mono text-paper" title={`${target.upstream_id} / ${target.account_id ?? ''}`}>
                {shortId(target.upstream_id)} / {shortId(target.account_id)}
              </span>
            </Td>
            <Td mono>{String(target.rpm)}</Td>
            <Td mono>{String(target.tpm)}</Td>
            <Td>
              <span className="block font-mono text-dense">
                {formatCap(
                  target.rpm_cap,
                  target.rpm_cap_source,
                  target.inferred_confidence,
                  t('tenant_routing.load.runtime.unlimited'),
                  (source) => t(`tenant_routing.load.runtime.cap_source.${source}`),
                  (value) => t('tenant_routing.load.runtime.confidence', { value }),
                )}
              </span>
              <span className="mt-0.5 block font-mono text-micro text-paper-low">
                {formatCap(
                  target.tpm_cap,
                  target.tpm_cap_source,
                  target.inferred_confidence,
                  t('tenant_routing.load.runtime.unlimited'),
                  (source) => t(`tenant_routing.load.runtime.cap_source.${source}`),
                  (value) => t('tenant_routing.load.runtime.confidence', { value }),
                )}
              </span>
            </Td>
            <Td mono>{String(target.shed)}</Td>
            <Td>
              <Chip tone={BREAKER_TONE[target.breaker_state]} lamp={false}>
                {t(`tenant_routing.load.runtime.breaker.${target.breaker_state}`)}
              </Chip>
            </Td>
            <Td mono>
              {target.ttft_ewma_ms === null ? '—' : String(target.ttft_ewma_ms)}
            </Td>
            <Td mono>{String(target.rehomed_sessions ?? 0)}</Td>
            <Td mono>{String(target.r429_rehomed ?? 0)}</Td>
            <Td>
              <EffectiveLayersCell layers={target.effective_layers ?? []} />
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

const LAYER_SCOPE_LABEL: Record<RoutingLoadEffectiveLayerEntry['scope'], I18nKey> = {
  sku: 'tenant_routing.load.runtime.layers.sku',
  namespace: 'tenant_routing.load.runtime.layers.namespace',
  global: 'tenant_routing.load.runtime.layers.global',
  unknown: 'tenant_routing.load.runtime.layers.unknown',
};

const LAYER_SCOPE_TONE: Record<RoutingLoadEffectiveLayerEntry['scope'], ChipTone> = {
  sku: 'signal',
  namespace: 'ok',
  global: 'neutral',
  unknown: 'warn',
};

function EffectiveLayersCell({
  layers,
}: {
  readonly layers: RoutingLoadEffectiveLayers;
}): JSX.Element {
  const t = useT();
  if (layers.length === 0) {
    return <span className="text-paper-low">—</span>;
  }
  return (
    <div className="flex flex-col gap-1.5" data-testid="routing-load-target-layers">
      {layers.map((entry) => {
        const key = layerEntryKey(entry);
        const unknown = isUnknownLayerScope(entry.scope);
        return (
          <div
            key={key}
            className="flex flex-col gap-1"
            data-testid={`routing-load-target-layer-${key}`}
          >
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <Chip tone={LAYER_SCOPE_TONE[entry.scope]} lamp={false}>
                {t(LAYER_SCOPE_LABEL[entry.scope])}
              </Chip>
              {entry.sku_id !== null ? (
                <span className="font-mono text-micro text-paper-low">{shortId(entry.sku_id)}</span>
              ) : null}
              {entry.namespace !== null && !unknown ? (
                <span className="font-mono text-micro text-paper-low">{entry.namespace}</span>
              ) : null}
            </span>
            <span className="flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-micro text-paper">
              <LayerFlag
                testId={`routing-load-target-layer-${key}-l2`}
                label={t('tenant_routing.load.strip.l2')}
                on={entry.l2_enabled}
              />
              <LayerFlag
                testId={`routing-load-target-layer-${key}-l3`}
                label={t('tenant_routing.load.strip.l3')}
                on={entry.l3_enabled}
              />
              <LayerFlag
                testId={`routing-load-target-layer-${key}-l4`}
                label={t('tenant_routing.load.strip.l4')}
                on={entry.l4_enabled}
              />
              <LayerFlag
                testId={`routing-load-target-layer-${key}-inferred`}
                label={t('tenant_routing.load.runtime.cap_source.inferred')}
                on={entry.l3_inferred_caps_enabled}
              />
              <LayerFlag
                testId={`routing-load-target-layer-${key}-rehome`}
                label={t('tenant_routing.load.settings.field.session_rehome_enabled')}
                on={entry.session_rehome_enabled}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LayerFlag({
  testId,
  label,
  on,
}: {
  readonly testId: string;
  readonly label: string;
  readonly on: boolean;
}): JSX.Element {
  const t = useT();
  return (
    <span data-testid={testId}>
      {label} {on ? t('tenant_routing.load.scope.tri.on') : t('tenant_routing.load.scope.tri.off')}
    </span>
  );
}
